/**
 * Error Service
 * Centralized error handling service for the SenditSell application
 *
 * Features:
 * - Categorizes errors with specific error codes
 * - Provides translated error messages (i18n support)
 * - Shows notifications automatically using Ant Design
 * - Extracts field-specific errors for forms
 * - Handles Axios, Zod validation, and custom errors
 */

import { message, notification } from 'antd';
import { AxiosError } from 'axios';
import { ZodError } from 'zod';
import i18n from '@/i18n/config';

/**
 * Error Codes - Categorized by type
 */
export type ErrorCode =
  // API Errors
  | 'API_NETWORK_ERROR'
  | 'API_TIMEOUT'
  | 'API_UNAUTHORIZED'
  | 'API_FORBIDDEN'
  | 'API_NOT_FOUND'
  | 'API_CONFLICT'
  | 'API_VALIDATION_ERROR'
  | 'API_SERVER_ERROR'
  | 'API_UNKNOWN_ERROR'
  // Validation Errors
  | 'VALIDATION_REQUIRED_FIELD'
  | 'VALIDATION_INVALID_FORMAT'
  | 'VALIDATION_MIN_LENGTH'
  | 'VALIDATION_MAX_LENGTH'
  | 'VALIDATION_INVALID_TYPE'
  // Business Logic Errors
  | 'VENDOR_ALREADY_EXISTS'
  | 'CATEGORY_IN_USE'
  | 'INSUFFICIENT_PERMISSIONS'
  | 'RESOURCE_IN_USE'
  // File Errors
  | 'FILE_TOO_LARGE'
  | 'FILE_INVALID_TYPE'
  | 'FILE_UPLOAD_FAILED';

/**
 * Error Context - Additional information about the error
 */
export interface ErrorContext {
  field?: string;
  value?: unknown;
  limit?: number | string;
  expected?: string;
  actual?: string;
  details?: Record<string, unknown>;
}

/**
 * Error Information - Standardized error object
 */
export interface ErrorInfo {
  code: ErrorCode;
  message: string;
  context?: ErrorContext;
  originalError?: unknown;
}

/**
 * Error Severity Levels
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * Error Service Class
 */
export class ErrorService {
  /**
   * Get error code from various error types
   */
  private static getErrorCode(error: unknown): ErrorCode {
    // Handle Axios errors
    if (error instanceof AxiosError) {
      if (!error.response) {
        return error.code === 'ECONNABORTED' ? 'API_TIMEOUT' : 'API_NETWORK_ERROR';
      }

      switch (error.response.status) {
        case 401:
          return 'API_UNAUTHORIZED';
        case 403:
          return 'API_FORBIDDEN';
        case 404:
          return 'API_NOT_FOUND';
        case 409:
          return 'API_CONFLICT';
        case 422:
          return 'API_VALIDATION_ERROR';
        case 500:
        case 502:
        case 503:
          return 'API_SERVER_ERROR';
        default:
          return 'API_UNKNOWN_ERROR';
      }
    }

    // Handle Zod validation errors
    if (error instanceof ZodError) {
      const firstError = error.errors[0];
      if (!firstError) return 'VALIDATION_INVALID_FORMAT';

      switch (firstError.code) {
        case 'invalid_type':
          return firstError.expected === 'never'
            ? 'VALIDATION_REQUIRED_FIELD'
            : 'VALIDATION_INVALID_TYPE';
        case 'too_small':
          return 'VALIDATION_MIN_LENGTH';
        case 'too_big':
          return 'VALIDATION_MAX_LENGTH';
        default:
          return 'VALIDATION_INVALID_FORMAT';
      }
    }

    // Handle Error objects with custom codes
    if (error instanceof Error && 'code' in error) {
      return (error as Error & { code: ErrorCode }).code;
    }

    return 'API_UNKNOWN_ERROR';
  }

  /**
   * Extract error context from various error types
   */
  private static getErrorContext(error: unknown): ErrorContext | undefined {
    // Handle Zod errors
    if (error instanceof ZodError) {
      const firstError = error.errors[0];
      if (!firstError) return undefined;

      // Safely access properties that may not exist on all ZodIssue types
      const expected = 'expected' in firstError ? String(firstError.expected) : undefined;
      const actual = 'received' in firstError ? String(firstError.received) : undefined;

      return {
        field: firstError.path.join('.'),
        expected,
        actual,
        value: firstError.message,
      };
    }

    // Handle Axios errors with details
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { details?: ErrorContext };
      return data.details;
    }

    return undefined;
  }

  /**
   * Get translated error message
   */
  private static getTranslatedMessage(code: ErrorCode, context?: ErrorContext): string {
    const namespace = 'common';

    // Try specific error message
    const specificKey = `errors.${code}`;
    if (i18n.exists(`${namespace}:${specificKey}`)) {
      return i18n.t(`${namespace}:${specificKey}`, context as Record<string, unknown>);
    }

    // Fallback to category generic message
    const category = code.split('_')[0];
    const genericKey = `errors.${category}_GENERIC`;
    if (i18n.exists(`${namespace}:${genericKey}`)) {
      return i18n.t(`${namespace}:${genericKey}`, context as Record<string, unknown>);
    }

    // Final fallback
    return i18n.t(`${namespace}:errors.UNKNOWN_ERROR`, 'An unexpected error occurred');
  }

  /**
   * Parse error into standardized ErrorInfo object
   */
  static parseError(error: unknown): ErrorInfo {
    const code = this.getErrorCode(error);
    const context = this.getErrorContext(error);

    // Get message from error or translation
    let message: string;
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { message?: string; error?: string };
      message = data.message || data.error || this.getTranslatedMessage(code, context);
    } else if (error instanceof Error) {
      message = error.message || this.getTranslatedMessage(code, context);
    } else {
      message = this.getTranslatedMessage(code, context);
    }

    return {
      code,
      context,
      message,
      originalError: error,
    };
  }

  /**
   * Get error severity from error code
   */
  static getSeverity(code: ErrorCode): ErrorSeverity {
    if (code.startsWith('API_SERVER') || code === 'API_NETWORK_ERROR') {
      return ErrorSeverity.CRITICAL;
    }
    if (code.startsWith('VALIDATION_') || code === 'API_NOT_FOUND') {
      return ErrorSeverity.INFO;
    }
    if (code === 'API_TIMEOUT') {
      return ErrorSeverity.WARNING;
    }
    return ErrorSeverity.ERROR;
  }

  /**
   * Show error notification
   * @deprecated Use useErrorHandler().handleError() instead for context-aware theming
   */
  static showError(
    error: unknown,
    options?: {
      title?: string;
      duration?: number;
      onClose?: () => void;
    }
  ): void {
    const errorInfo = this.parseError(error);
    const severity = this.getSeverity(errorInfo.code);

    // Use message.error for simple messages, notification for complex ones
    if (severity === ErrorSeverity.CRITICAL || options?.title) {
      notification.error({
        message: options?.title || i18n.t('common:errors.errorTitle', 'Error'),
        description: errorInfo.message,
        duration: options?.duration ?? (severity === ErrorSeverity.CRITICAL ? 0 : 5),
        onClose: options?.onClose,
      });
    } else {
      message.error(errorInfo.message, options?.duration ?? 5);
    }

    // Log error in development
    if (import.meta.env.DEV) {
      console.error('[Error Service]', {
        code: errorInfo.code,
        message: errorInfo.message,
        context: errorInfo.context,
        originalError: error,
      });
    }
  }

  /**
   * Show success notification
   * @deprecated Use useErrorHandler().handleSuccess() instead for context-aware theming
   */
  static showSuccess(
    messageText: string | { key: string; options?: Record<string, unknown> },
    options?: {
      duration?: number;
    }
  ): void {
    const translatedMessage =
      typeof messageText === 'string'
        ? messageText
        : i18n.t(`common:${messageText.key}`, messageText.options);

    message.success(translatedMessage, options?.duration ?? 3);
  }

  /**
   * Show warning notification
   * @deprecated Use useErrorHandler().handleWarning() instead for context-aware theming
   */
  static showWarning(
    messageText: string,
    options?: {
      duration?: number;
    }
  ): void {
    message.warning(messageText, options?.duration ?? 4);
  }

  /**
   * Show info notification
   * @deprecated Use useErrorHandler().handleInfo() instead for context-aware theming
   */
  static showInfo(
    messageText: string,
    options?: {
      duration?: number;
    }
  ): void {
    message.info(messageText, options?.duration ?? 3);
  }

  /**
   * Extract field-specific error from Zod validation error
   */
  static getFieldError(error: unknown, fieldName: string): string | undefined {
    if (!(error instanceof ZodError)) return undefined;

    const fieldError = error.errors.find((err) => err.path.join('.') === fieldName);

    if (!fieldError) return undefined;

    const context: ErrorContext = {
      field: fieldName,
      value: fieldError.message,
    };

    return this.getTranslatedMessage(this.getErrorCode(error), context);
  }

  /**
   * Check if error is a network error
   */
  static isNetworkError(error: unknown): boolean {
    const errorInfo = this.parseError(error);
    return errorInfo.code === 'API_NETWORK_ERROR' || errorInfo.code === 'API_TIMEOUT';
  }

  /**
   * Check if error is an authentication error
   */
  static isAuthError(error: unknown): boolean {
    const errorInfo = this.parseError(error);
    return errorInfo.code === 'API_UNAUTHORIZED' || errorInfo.code === 'API_FORBIDDEN';
  }

  /**
   * Check if error is a validation error
   */
  static isValidationError(error: unknown): boolean {
    if (error instanceof ZodError) return true;
    if (error instanceof AxiosError) {
      return error.response?.status === 422;
    }
    return false;
  }

  /**
   * Create a custom error with error code
   */
  static createError(code: ErrorCode, context?: ErrorContext): Error {
    const message = this.getTranslatedMessage(code, context);
    const error = new Error(message) as Error & { code: ErrorCode; context?: ErrorContext };
    error.code = code;
    error.context = context;
    return error;
  }

  /**
   * Handle async operation with automatic error handling
   */
  static async withErrorHandling<T>(
    operation: () => Promise<T>,
    options?: {
      showNotification?: boolean;
      onError?: (error: unknown) => void;
    }
  ): Promise<T | null> {
    try {
      return await operation();
    } catch (error) {
      if (options?.showNotification !== false) {
        this.showError(error);
      }
      options?.onError?.(error);
      return null;
    }
  }
}

export default ErrorService;
